import dayjs, { Dayjs } from 'dayjs';
import { ProductProjectPlanItem } from 'library/types';
import { AddProjectStepListItem, CreateProjectPlanRow } from './types';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM, PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM } from 'enums';

export const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const normalizeRowOrders = (rows: CreateProjectPlanRow[]): CreateProjectPlanRow[] =>
  rows.map((row, index) => ({
    ...row,
    orderNo: index + 1,
  }));

export const createEmptyProjectPlanRow = (
  bookId: number | null,
  projectId: number | null,
  projectStartDate: Dayjs | null,
  orderNo: number
): CreateProjectPlanRow => ({
  tempId: crypto.randomUUID(),
  orderNo,
  bookId,
  projectId,
  title: '',
  description: '',
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
  relatedContactId: null,
  alternativeRelatedContactId: null,
  responsibleContactType: null,
  responsibleCustomerFullName: '',
  estimatedTaskDay: null,
  additionalEffortDay: null,
  dueDate: null,
  notes: '',
});

export const mapProductProjectPlansToRows = ({
  items,
  bookId,
  projectId,
  projectStartDate,
  defaultRelatedContactId = null,
  defaultAlternativeRelatedContactId = null,
}: {
  items: ProductProjectPlanItem[];
  bookId: number | null;
  projectId: number | null;
  projectStartDate: Dayjs | null;
  defaultRelatedContactId?: number | null;
  defaultAlternativeRelatedContactId?: number | null;
}): CreateProjectPlanRow[] => {
  const rows: CreateProjectPlanRow[] = items.map((item, index) => {
    const contactType = item.responsibleContactType ?? null;
    const shouldApplyDefaults =
      contactType !== null && contactType !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER;

    return {
      tempId: crypto.randomUUID(),
      orderNo: index + 1,
      bookId,
      projectId,
      title: item.title ?? '',
      description: item.description ?? '',
      priority: item.priority ?? PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
      relatedContactId: shouldApplyDefaults ? defaultRelatedContactId : null,
      alternativeRelatedContactId: shouldApplyDefaults ? defaultAlternativeRelatedContactId : null,
      responsibleContactType: contactType,
      responsibleCustomerFullName: '',
      estimatedTaskDay: toNullableNumber(item.estimatedTaskDay),
      additionalEffortDay: null,
      dueDate: null,
      notes: '',
    };
  });

  return recalculateDueDates({
    rows,
    projectStartDate,
  });
};

export const recalculateDueDates = ({
  rows,
  projectStartDate,
}: {
  rows: CreateProjectPlanRow[];
  projectStartDate: Dayjs | null;
}): CreateProjectPlanRow[] => {
  if (!projectStartDate) {
    return rows.map((row) => ({
      ...row,
      dueDate: null,
    }));
  }

  let cursor = projectStartDate.startOf('day');

  return rows.map((row) => {
    const estimated = toNullableNumber(row.estimatedTaskDay);

    if (estimated !== null && estimated > 0) {
      cursor = cursor.add(estimated, 'day');
    }

    return {
      ...row,
      dueDate: cursor.toISOString(),
    };
  });
};

export const mapRowsToAddListPayload = (rows: CreateProjectPlanRow[]): AddProjectStepListItem[] =>
  rows.map((row) => ({
    orderNo: row.orderNo,
    bookId: Number(row.bookId),
    projectId: Number(row.projectId),
    title: row.title.trim(),
    description: row.description.trim(),
    priority: Number(row.priority),
    relatedContactId: row.relatedContactId ?? null,
    alternativeRelatedContactId: row.alternativeRelatedContactId ?? null,
    responsibleContactType: row.responsibleContactType ?? null,
    responsibleCustomerFullName:
      row.responsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.IDECON
        ? ''
        : row.responsibleCustomerFullName.trim(),
    estimatedTaskDay: toNullableNumber(row.estimatedTaskDay),
    additionalEffortDay: toNullableNumber(row.additionalEffortDay),
    dueDate: row.dueDate ? dayjs(row.dueDate).toISOString() : null,
    notes: row.notes.trim(),
    comments: [],
    parentTaskId: null,
  }));

export const validateProjectPlanBeforeSubmit = ({
  projectStartDate,
  bookId,
  projectId,
  rows,
  language,
}: {
  projectStartDate: Dayjs | null;
  bookId: number | null;
  projectId: number | null;
  rows: CreateProjectPlanRow[];
  language: string;
}): string | null => {
  if (!projectStartDate)
    return language === 'tr' ? 'Lütfen proje başlangıç tarihi seçiniz.' : 'Select the project start date';
  if (!bookId) return language === 'tr' ? 'Lütfen proje seçiniz.' : 'Select a project';
  if (!projectId) return language === 'tr' ? 'Lütfen ürün seçiniz.' : 'Select a product';
  if (!rows.length)
    return language === 'tr' ? 'Kaydedilecek en az 1 satır bulunmalıdır.' : 'Required at least one line to save';

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const line = i + 1;

    if (!row.title.trim())
      return language === 'tr'
        ? `${line}. satırda proje adımı boş bırakılamaz.`
        : `The project step of the ${line}. line cannot be left blank.`;

    if (row.title.trim().length > 50)
      return language === 'tr'
        ? `${line}. satırda proje adımı için en fazla 50 karakter girilebilir.`
        : `A maximum of 50 characters can be entered for project step at line ${line}.`;

    if (!row.description.trim())
      return language === 'tr'
        ? `${line}. satırda adım açıklaması boş bırakılamaz.`
        : `The step description of the ${line}. line cannot be left blank.`;

    if (row.description.trim().length > 250)
      return language === 'tr'
        ? `${line}. satırda adım açıklaması için en fazla 250 karakter girilebilir.`
        : `A maximum of 250 characters can be entered for step description at line ${line}.`;

    if (!row.priority)
      return language === 'tr'
        ? `${line}. satırda öncelik boş bırakılamaz.`
        : `The priority of the ${line}. line cannot be left blank.`;

    if (row.responsibleContactType !== PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER && !row.relatedContactId)
      return language === 'tr'
        ? `${line}. satırda ilgili kişi boş bırakılamaz.`
        : `The related contact of the ${line}. line cannot be left blank.`;

    if (row.estimatedTaskDay !== null && row.estimatedTaskDay !== '') {
      const estimated = Number(row.estimatedTaskDay);
      if (Number.isNaN(estimated) || estimated < 0) {
        return language === 'tr'
          ? `${line}. satırda tahmini adım süresi geçersizdir.`
          : `The estimated step duration is invalid on line ${line}.`;
      }
    }

    if (row.additionalEffortDay !== null && row.additionalEffortDay !== '') {
      const additional = Number(row.additionalEffortDay);
      if (Number.isNaN(additional) || additional < 0) {
        return language === 'tr'
          ? `${line}. satırda ek efor süresi geçersizdir.`
          : `Additional effort time is invalid on line ${line}.`;
      }
    }

    if (!row.responsibleContactType)
      return language === 'tr'
        ? `${line}. satırda sorumlu boş bırakılamaz.`
        : `The responsible contact of the ${line}. line cannot be left blank.`;

    if (
      (row.responsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER ||
        row.responsibleContactType === PROJECT_STEP_RESPONSIBLE_CONTACT_TYPE_ENUM.CUSTOMER_AND_IDECON) &&
      !row.responsibleCustomerFullName.trim()
    )
      return language === 'tr'
        ? `${line}. satırda müşteri sorumlu ad soyad boş bırakılamaz.`
        : `The responsible customer full name of the ${line}. line cannot be left blank.`;

    if (row.notes.trim().length > 250)
      return language === 'tr'
        ? `${line}. satırda notlar için en fazla 250 karakter girilebilir.`
        : `A maximum of 250 characters can be entered for notes at line ${line}.`;
  }

  return null;
};

export const isRowsDirtyComparedToTemplate = ({
  rows,
  templateRows,
}: {
  rows: CreateProjectPlanRow[];
  templateRows: CreateProjectPlanRow[];
}) => {
  const sanitize = (list: CreateProjectPlanRow[]) => list.map(({ tempId, ...rest }) => rest);

  return JSON.stringify(sanitize(rows)) !== JSON.stringify(sanitize(templateRows));
};
